import { useState } from "react"
import { Card } from "@/components/Card"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { useToast } from "@/components/Toast"
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline"

interface LimitAdjustmentFormProps {
  teamId: string
  currentLimits: {
    storage_gb: number
    max_games: number
    max_team_members?: number
  }
  isEnterprise: boolean
  onUpdate?: () => void
}

export function LimitAdjustmentForm({
  teamId: _teamId,
  currentLimits,
  isEnterprise,
  onUpdate
}: LimitAdjustmentFormProps) {
  const { toast } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [limits, setLimits] = useState({
    ...currentLimits,
    max_team_members: currentLimits.max_team_members ?? 10
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isEnterprise) {
    return null
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      // TODO: Implement API call to update team limits
      // await updateTeamLimits(_teamId, limits)
      
      toast({
        title: "Limits updated",
        description: "Team limits have been adjusted successfully.",
      })
      
      setIsEditing(false)
      onUpdate?.()
    } catch {
      toast({
        title: "Error",
        description: "Failed to update team limits. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setLimits({
      ...currentLimits,
      max_team_members: currentLimits.max_team_members ?? 10
    })
    setIsEditing(false)
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <AdjustmentsHorizontalIcon className="h-5 w-5 text-secondary" />
          <h3 className="text-lg font-semibold">Manual Limit Adjustment</h3>
        </div>
        {!isEditing && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            Adjust Limits
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Storage Limit (GB)
            </label>
            <Input
              type="number"
              value={limits.storage_gb}
              onChange={(e) => setLimits({ ...limits, storage_gb: parseInt(e.target.value) || 0 })}
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Max Games
            </label>
            <Input
              type="number"
              value={limits.max_games === -1 ? "" : limits.max_games}
              onChange={(e) => setLimits({ ...limits, max_games: e.target.value ? parseInt(e.target.value) : -1 })}
              placeholder="Leave empty for unlimited"
              min="1"
            />
          </div>

          {currentLimits.max_team_members !== undefined && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Team Members
              </label>
              <Input
                type="number"
                value={limits.max_team_members === -1 ? "" : limits.max_team_members}
                onChange={(e) => setLimits({ ...limits, max_team_members: e.target.value ? parseInt(e.target.value) : -1 })}
                placeholder="Leave empty for unlimited"
                min="1"
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-secondary">
          <p>This enterprise team has custom limits that can be adjusted manually.</p>
          <p className="mt-2">Current configuration allows for flexible resource allocation.</p>
        </div>
      )}
    </Card>
  )
}